import { z } from 'zod';

const calendar_week_totals = z
  .object({
    critical: z.number().int(),
    at_risk: z.number().int(),
    filled: z.number().int(),
  })
  .passthrough();
const shift_ribbon = z
  .object({
    type: z.enum([
      'needs_approval',
      'in_progress',
      'starts_in',
      'ended_unfilled',
    ]),
    hours: z.number().nullish(),
  })
  .passthrough();
const shift_card = z
  .object({
    id: z.number().int(),
    job_id: z.number().int(),
    order_id: z.number().int().nullish(),
    order_uuid: z.string().nullish(),
    client_id: z.number().int().nullish(),
    client_name: z.string().nullish(),
    venue_name: z.string().nullish(),
    billing_account_id: z.number().int().nullish(),
    billing_account_name: z.string().nullish(),
    skill_name: z.string().nullish(),
    location_code: z.string().nullish(),
    address_line1: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    zip: z.string().nullish(),
    timezone: z.string().nullish(),
    start_at: z.string().nullish(),
    end_at: z.string().nullish(),
    hired_count: z.number().int(),
    quantity: z.number().int(),
    fill_status: z.enum(['filled', 'at_risk', 'critical']).nullable(),
    job_type: z.string().nullish(),
    restricted_pool: z.boolean(),
    dependability_drop: z.boolean(),
    ribbon: shift_ribbon.nullish(),
  })
  .passthrough();
const calendar_day = z
  .object({
    date: z.string(),
    weekday: z.string(),
    is_today: z.boolean(),
    fill_rate_pct: z.number().int().nullish(),
    shift_count: z.number().int(),
    shifts: z.array(shift_card),
  })
  .passthrough();
const calendar_week_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        start_date: z.string(),
        end_date: z.string(),
        time_zone: z.string(),
        totals: calendar_week_totals,
        days: z.array(calendar_day),
      })
      .passthrough(),
  })
  .passthrough();
const error_response = z
  .object({ success: z.literal(false), errors: z.array(z.string()) })
  .passthrough();
const summary_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        no_show: z.number().int(),
        missing_timesheet: z.number().int(),
        needs_approval: z.number().int(),
      })
      .passthrough(),
  })
  .passthrough();
const pagination = z
  .object({
    total: z.number().int(),
    page: z.number().int(),
    per_page: z.number().int(),
    total_pages: z.number().int(),
  })
  .passthrough();
const no_show_worker = z
  .object({
    id: z.number().int(),
    worker_id: z.number().int(),
    name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    phone: z.string().nullish(),
    status: z.enum([
      'no_show',
      'awaiting_confirmation',
      'backfill_requested',
      'escalated',
    ]),
    confirmed: z.boolean(),
    marked_automatically: z.boolean(),
    marked_by: z.string().nullish(),
    marked_at: z.string().nullish(),
  })
  .passthrough();
const no_show_shift = z
  .object({
    shift_id: z.number().int(),
    job_id: z.number().int(),
    order_id: z.number().int().nullish(),
    client_name: z.string().nullish(),
    skill_name: z.string().nullish(),
    location_code: z.string().nullish(),
    start_at: z.string().nullish(),
    end_at: z.string().nullish(),
    timezone: z.string().nullish(),
    workers: z.array(no_show_worker),
  })
  .passthrough();
const no_show_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        total: z.number().int(),
        pagination: pagination,
        confirmed_total: z.number().int().optional(),
        unresolved_total: z.number().int().optional(),
        shifts: z.array(no_show_shift),
      })
      .passthrough(),
  })
  .passthrough();
const missing_timesheet_worker = z
  .object({
    id: z.number().int(),
    timesheet_id: z.number().int().nullish(),
    job_worker_id: z.number().int().nullish(),
    worker_id: z.number().int(),
    name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    phone: z.string().nullish(),
    status: z.literal('missing_timesheet'),
    check_in: z.string().nullish(),
    check_out: z.string().nullish(),
    break_minutes: z.number().int().nullish(),
    total_minutes: z.number().int().nullish(),
    average_rating: z.number().nullish(),
    jobs_rating_count: z.number().int().nullish(),
  })
  .passthrough();
const missing_timesheet_shift = z
  .object({
    shift_id: z.number().int(),
    job_id: z.number().int(),
    order_id: z.number().int().nullish(),
    client_name: z.string().nullish(),
    skill_name: z.string().nullish(),
    location_code: z.string().nullish(),
    start_at: z.string().nullish(),
    end_at: z.string().nullish(),
    timezone: z.string().nullish(),
    workers: z.array(missing_timesheet_worker),
  })
  .passthrough();
const missing_timesheet_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        total: z.number().int(),
        pagination: pagination,
        shifts: z.array(missing_timesheet_shift),
      })
      .passthrough(),
  })
  .passthrough();
// GRAVY-10366 — one side of a worker's timesheet on the expanded roster.
// `distance_miles` is null when the side has no coordinates: the cell renders an
// em dash rather than "0.0 mi away", which would read as "stood on the spot".
const needs_approval_check_event = z
  .object({
    at: z.string().nullish(),
    verification_method: z.string().nullish(),
    distance_feet: z.number().int().nullish(),
    distance_miles: z.number().nullish(),
    // GRAVY-10825. Whether the side was captured at a location — what draws the
    // map pin on a non-QR capture. `verification_method` cannot answer it: it
    // holds only `qr`, `skipped`, `manual_override` or null, so an in-app GPS
    // check-in looks identical to an ops-typed time under it. Optional so a
    // frontend deployed ahead of graphy falls back to the pre-10825 rendering
    // (no pin) rather than failing its schema parse and blanking the tab.
    has_coordinates: z.boolean().nullish(),
    flagged: z.boolean(),
  })
  .passthrough();

const needs_approval_worker = z
  .object({
    timesheet_id: z.number().int(),
    worker_id: z.number().int().nullish(),
    worker_name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    checkin: needs_approval_check_event,
    checkout: needs_approval_check_event,
    total_hours: z.number().nullish(),
  })
  .passthrough();

const needs_approval_shift = z
  .object({
    shift_id: z.number().int(),
    job_id: z.number().int(),
    order_id: z.number().int().nullish(),
    client_name: z.string().nullish(),
    skill_name: z.string().nullish(),
    location_code: z.string().nullish(),
    start_at: z.string().nullish(),
    end_at: z.string().nullish(),
    timezone: z.string().nullish(),
    hired_count: z.number().int(),
    quantity: z.number().int(),
    // GRAVY-10366. Optional so a frontend deployed ahead of graphy still
    // renders the row rather than failing its schema parse and blanking the tab.
    status: z.enum(['needs_approval', 'needs_review']).nullish(),
    status_subtext: z.string().nullish(),
    location_flag_count: z.number().int().nullish(),
    workers: z.array(needs_approval_worker).nullish(),
  })
  .passthrough();
// GRAVY-10366 — the Review modal.
const location_flag_point = z
  .object({ lat: z.number(), lng: z.number() })
  .passthrough();

const review_check_event = z
  .object({
    at: z.string().nullish(),
    verification_method: z.string().nullish(),
    distance_feet: z.number().int().nullish(),
    distance_miles: z.number().nullish(),
    flagged: z.boolean(),
    // Present only on a flagged side — a clean side has no flag row, so no map.
    location_flag: z
      .object({
        id: z.number().int(),
        side: z.string(),
        headline: z.string(),
        distance_label: z.string(),
        radius_feet: z.number().int(),
        venue_name: z.string().nullish(),
        venue_address: z.string(),
        anchor: location_flag_point.nullish(),
        flagged_point: location_flag_point.nullish(),
      })
      .passthrough()
      .nullish(),
  })
  .passthrough();

const timesheet_review_response = z
  .object({
    success: z.literal(true),
    message: z.string().nullish(),
    data: z
      .object({
        timesheet_id: z.number().int(),
        job_id: z.number().int().nullish(),
        shift_id: z.number().int().nullish(),
        tag: z.string().nullish(),
        worker: z
          .object({
            id: z.number().int(),
            name: z.string().nullish(),
            avatar_url: z.string().nullish(),
          })
          .passthrough()
          .nullish(),
        similar: z
          .object({
            window_days: z.number().int().nullish(),
            total_count: z.number().int(),
            unresolved_count: z.number().int(),
          })
          .passthrough(),
        checkin: review_check_event,
        checkout: review_check_event,
        break_minutes: z.number().int(),
        net_hours: z.number().nullish(),
      })
      .passthrough(),
  })
  .passthrough();

const needs_approval_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        total: z.number().int(),
        pagination: pagination,
        shifts: z.array(needs_approval_shift),
      })
      .passthrough(),
  })
  .passthrough();
const shift_detail_supervisor = z
  .object({ name: z.string().nullable(), phone: z.string().nullable() })
  .partial()
  .passthrough();
const shift_detail_worker = z
  .object({
    worker_id: z.number().int().nullish(),
    name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    dependability_score: z.number().int(),
    below_gate: z.boolean(),
    gate_reason: z
      .enum([
        'eligible',
        'insufficient_shifts',
        'active_violation',
        'lta_exempt',
        'favorite_exempt',
      ])
      .nullish(),
    tier: z.enum(['below', 'qualifying', 'unevaluated']),
    status: z.string().nullish(),
    status_label: z.string().nullish(),
    hired: z.boolean(),
    dropped: z.boolean().optional(),
    dropped_at: z.string().nullish(),
    removed: z.boolean().optional(),
    removed_at: z.string().nullish(),
    no_show: z.boolean().optional(),
    no_show_at: z.string().nullish(),
  })
  .passthrough();
const shift_detail_invite = z
  .object({
    worker_id: z.number().int().nullish(),
    name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    status: z.string(),
    status_label: z.string(),
    invited_at: z.string().nullish(),
    declined_at: z.string().nullish(),
    expired_at: z.string().nullish(),
    dependability_score: z.number().int().nullish(),
    below_gate: z.boolean().nullish(),
    tier: z.string().nullish(),
    hire_eligible: z.boolean().optional(),
    hire_blocked_reason: z.string().nullish(),
    hire_blocked_label: z.string().nullish(),
  })
  .passthrough();
const shift_detail_activity = z
  .object({
    id: z.number().int(),
    event_type: z.string(),
    title: z.string().nullish(),
    avatar_url: z.string().nullish(),
    worker_name: z.string().nullish(),
    occurred_at: z.string().nullish(),
    actor_name: z.string().nullish(),
    actor_avatar_url: z.string().nullish(),
    automated: z.boolean().optional(),
    location_flagged: z.boolean().nullish(),
    detail: z.string().nullish(),
    segments: z
      .array(
        z
          .object({
            type: z.enum([
              'actor',
              'text',
              'worker',
              'workers',
              'strong',
              'quote',
            ]),
            text: z.string(),
            worker_id: z.number().int().nullish(),
            worker_ids: z.array(z.number().int()).nullish(),
            names: z.array(z.string()).nullish(),
          })
          .passthrough(),
      )
      .nullish(),
  })
  .passthrough();
const shift_detail_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        job_id: z.number().int(),
        order_id: z.number().int().nullish(),
        client_id: z.number().int().nullish(),
        client_name: z.string().nullish(),
        venue_name: z.string().nullish(),
        billing_account_id: z.number().int().nullish(),
        billing_account_name: z.string().nullish(),
        logo_url: z.string().nullish(),
        status: z.string().nullish(),
        status_label: z.string().nullish(),
        fill_status: z.enum(['filled', 'at_risk', 'critical']).nullish(),
        restricted_pool: z.boolean().optional(),
        job_type: z.string().nullish(),
        bg_check_required: z.boolean().optional(),
        dependability_gate: z
          .enum(['strict', 'relaxed', 'off', 'unknown'])
          .optional(),
        dependability_gate_label: z.string().optional(),
        dependability_gate_enabled: z.boolean().optional(),
        dependability_gate_reason: z.string().nullish(),
        qr_enabled: z.boolean().optional(),
        worker_classification: z.string().nullish(),
        role: z.string().nullish(),
        start_at: z.string().nullish(),
        end_at: z.string().nullish(),
        timezone: z.string().nullish(),
        duration_hours: z.number().nullish(),
        address_line1: z.string().nullish(),
        city: z.string().nullish(),
        state: z.string().nullish(),
        zip: z.string().nullish(),
        supervisor: shift_detail_supervisor.nullish(),
        hired_count: z.number().int(),
        quantity: z.number().int(),
        percent_filled: z.number().int().optional(),
        gate_threshold: z.number().int().nullish(),
        workers: z.array(shift_detail_worker),
        invites: z.array(shift_detail_invite).optional(),
        recent_activity: z.array(shift_detail_activity).optional(),
      })
      .passthrough(),
  })
  .passthrough();
const shift_activity_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        activities: z.array(shift_detail_activity),
        pagination: pagination,
      })
      .passthrough(),
  })
  .passthrough();
const id_name_option = z
  .object({ id: z.number().int(), name: z.string().nullable() })
  .passthrough();
const value_label_option = z
  .object({ value: z.string(), label: z.string() })
  .passthrough();
const filters_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        clients: z.array(id_name_option),
        accounts: z.array(id_name_option),
        skills: z.array(id_name_option),
        regions: z.array(id_name_option),
        statuses: z.array(value_label_option),
        fill_rate_bands: z.array(value_label_option),
        shift_types: z.array(value_label_option),
      })
      .passthrough(),
  })
  .passthrough();

export const schemas = {
  needs_approval_worker,
  needs_approval_check_event,
  timesheet_review_response,
  calendar_week_totals,
  shift_ribbon,
  shift_card,
  calendar_day,
  calendar_week_response,
  error_response,
  summary_response,
  pagination,
  no_show_worker,
  no_show_shift,
  no_show_response,
  missing_timesheet_worker,
  missing_timesheet_shift,
  missing_timesheet_response,
  needs_approval_shift,
  needs_approval_response,
  shift_detail_supervisor,
  shift_detail_worker,
  shift_detail_invite,
  shift_detail_activity,
  shift_detail_response,
  shift_activity_response,
  id_name_option,
  value_label_option,
  filters_response,
};
